/**
 * Shop from Videos – Section behaviour
 * Handles: strip slider nav, card click → modal open, modal slide navigation,
 * video play/pause/mute, external video (iframe) lazy load, keyboard (Escape/arrows),
 * cart update → close modal. Works with Shopify section editor (shopify:section:load).
 */
(function () {
  /**
   * Initialize one .shop-from-videos section: modal, strip, and event listeners.
   * @param {Element} section - The section root (.shop-from-videos)
   */
  function initSection(section) {
    if (!section || section.dataset.sfvReady === 'true') return;
    section.dataset.sfvReady = 'true';

    const modal = section.querySelector('.shop-from-videos-modal');
    const track = section.querySelector('.shop-from-videos-modal__track');
    const slides = section.querySelectorAll('.shop-from-videos-modal__slide');
    const totalSlides = slides.length;
    if (!modal || !track || !totalSlides) return;

    const closeBtn = section.querySelector('.shop-from-videos-modal__close');
    const prevBtn = section.querySelector('.shop-from-videos-modal__nav--prev');
    const nextBtn = section.querySelector('.shop-from-videos-modal__nav--next');
    const stripSlider = section.querySelector('[data-strip-slider]');
    const stripPrev = section.querySelector('[data-strip-prev]');
    const stripNext = section.querySelector('[data-strip-next]');
    let currentIndex = 0;

    const videoWrapSelector = '.shop-from-videos-modal__video-wrap video';

    /** Pause all videos inside this section’s modal (shared by closeModal and goToSlide). */
    function pauseAllModalVideos() {
      section.querySelectorAll(videoWrapSelector).forEach(function (v) { v.pause(); });
    }

    /** Returns CSS translateX for the modal track (one full slide per step). */
    function getTrackTranslateX(index) {
      return -index * 100 + '%';
    }

    /** Open modal and show slide at given index; clamp index to valid range. */
    function openModal(index) {
      currentIndex = Math.max(0, Math.min(index, totalSlides - 1));
      modal.removeAttribute('hidden');
      modal.classList.add('is-open');
      modal.style.setProperty('display', 'flex');
      document.body.classList.add('shop-from-videos-modal-open');
      goToSlide(currentIndex);
    }

    /** Close modal, reset display/hidden, unlock body scroll, pause all modal videos. */
    function closeModal() {
      modal.classList.remove('is-open');
      modal.style.display = '';
      modal.setAttribute('hidden', '');
      document.body.classList.remove('shop-from-videos-modal-open');
      pauseAllModalVideos();
    }

    /**
     * Switch modal to slide at index: move track, update prev/next disabled state,
     * pause other videos, play current slide video (unmuted; fallback mute if autoplay blocked).
     */
    function goToSlide(index) {
      currentIndex = index;
      track.style.transform = 'translate3d(' + getTrackTranslateX(index) + ', 0, 0)';
      if (prevBtn) {
        prevBtn.disabled = index <= 0;
        prevBtn.setAttribute('aria-disabled', index <= 0);
        prevBtn.classList.toggle('is-disabled', index <= 0);
      }
      if (nextBtn) {
        nextBtn.disabled = index >= totalSlides - 1;
        nextBtn.setAttribute('aria-disabled', index >= totalSlides - 1);
        nextBtn.classList.toggle('is-disabled', index >= totalSlides - 1);
      }
      pauseAllModalVideos();
      const slide = slides[index];
      const video = slide && slide.querySelector('video');
      if (video) {
        video.currentTime = 0;
        video.volume = 1;
        video.muted = false;
        video.play().catch(function () { video.muted = true; video.play().catch(function () {}); });
      }
      const muteBtn = slide && slide.querySelector('[data-modal-mute]');
      if (muteBtn) {
        muteBtn.classList.remove('is-muted');
        muteBtn.setAttribute('aria-label', 'Mute');
      }
    }

    /**
     * Lazy-load external video: inject iframe from data-iframe-src and hide the poster button.
     * @param {Element} btn - The poster/play button with data-load-iframe (inside [data-iframe-src] container)
     */
    function loadExternalVideo(btn) {
      const container = btn.closest('.shop-from-videos-modal__video[data-iframe-src]');
      const src = container && container.getAttribute('data-iframe-src');
      if (!src) return;
      let iframe = container.querySelector('iframe');
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.setAttribute('src', src);
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.className = 'shop-from-videos-modal__iframe';
        container.appendChild(iframe);
      } else {
        iframe.setAttribute('src', src);
      }
      iframe.style.display = 'block';
      btn.style.display = 'none';
    }

    function getStripScrollStep() {
      const firstCard = stripSlider && stripSlider.querySelector('.shop-from-videos__card');
      const gap = stripSlider ? (parseInt(getComputedStyle(stripSlider).gap, 10) || 16) : 16;
      return firstCard ? firstCard.offsetWidth + gap : 300;
    }

    function updateStripNav() {
      if (!stripSlider) return;
      const maxScroll = Math.max(0, stripSlider.scrollWidth - stripSlider.clientWidth);
      const canScroll = maxScroll > 4;
      const atStart = stripSlider.scrollLeft <= 4;
      const atEnd = stripSlider.scrollLeft >= maxScroll - 4;
      if (stripPrev) {
        const hidePrev = !canScroll || atStart;
        stripPrev.classList.toggle('is-hidden', hidePrev);
        stripPrev.disabled = hidePrev;
        stripPrev.setAttribute('aria-hidden', hidePrev ? 'true' : 'false');
      }
      if (stripNext) {
        const hideNext = !canScroll || atEnd;
        stripNext.classList.toggle('is-hidden', hideNext);
        stripNext.disabled = hideNext;
        stripNext.setAttribute('aria-hidden', hideNext ? 'true' : 'false');
      }
    }

    if (stripSlider && (stripPrev || stripNext)) {
      if (stripPrev) {
        stripPrev.addEventListener('click', function () {
          stripSlider.scrollBy({ left: -getStripScrollStep(), behavior: 'smooth' });
        });
      }
      if (stripNext) {
        stripNext.addEventListener('click', function () {
          stripSlider.scrollBy({ left: getStripScrollStep(), behavior: 'smooth' });
        });
      }
      stripSlider.addEventListener('scroll', updateStripNav, { passive: true });
      window.addEventListener('resize', updateStripNav);
      window.addEventListener('load', updateStripNav);
      updateStripNav();
    }

    // --- Delegated click: card → open modal; mute button → toggle mute ---
    section.addEventListener('click', function (e) {
      const card = e.target.closest('.shop-from-videos__card[data-block-index]');
      if (card) {
        e.preventDefault();
        const i = parseInt(card.getAttribute('data-block-index'), 10);
        if (!isNaN(i)) openModal(i);
        return;
      }
      const muteBtn = e.target.closest('[data-modal-mute]');
      if (muteBtn) {
        const wrap = muteBtn.closest('.shop-from-videos-modal__video-wrap');
        const video = wrap && wrap.querySelector('video');
        if (video) {
          video.muted = !video.muted;
          muteBtn.classList.toggle('is-muted', video.muted);
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
        }
      }
    });

    // --- Modal: close button, backdrop, prev/next, poster → load iframe ---
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    const backdrop = section.querySelector('.shop-from-videos-modal__backdrop');
    if (backdrop) backdrop.addEventListener('click', closeModal);
    if (prevBtn) prevBtn.addEventListener('click', function () { if (currentIndex > 0) goToSlide(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { if (currentIndex < totalSlides - 1) goToSlide(currentIndex + 1); });
    section.querySelectorAll('.shop-from-videos-modal__video-poster[data-load-iframe]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        loadExternalVideo(btn);
      });
    });

    // --- Keyboard: Escape close; ArrowLeft/ArrowRight change slide ---
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeModal();
      else if (e.key === 'ArrowLeft' && currentIndex > 0) goToSlide(currentIndex - 1);
      else if (e.key === 'ArrowRight' && currentIndex < totalSlides - 1) goToSlide(currentIndex + 1);
    });

    // --- Resize: re-apply track position when modal is open (debounced) ---
    let resizeTid;
    window.addEventListener('resize', function () {
      if (!modal.classList.contains('is-open')) return;
      clearTimeout(resizeTid);
      resizeTid = setTimeout(function () { goToSlide(currentIndex); }, 100);
    });

    // Show section content (was hidden to avoid FOUC)
    section.classList.remove('shop-from-videos--uninitialized');
  }

  /**
   * Close a modal by element reference and pause its videos.
   * Used when we don’t have section scope (e.g. from pub/sub).
   * @param {Element} modal - The .shop-from-videos-modal element
   */
  function closeModalByElement(modalEl) {
    const section = modalEl && modalEl.closest('.shop-from-videos');
    if (!section) return;
    modalEl.classList.remove('is-open');
    modalEl.style.display = '';
    modalEl.setAttribute('hidden', '');
    document.body.classList.remove('shop-from-videos-modal-open');
    section.querySelectorAll('.shop-from-videos-modal__video-wrap video').forEach(function (v) { v.pause(); });
  }

  var cartUpdateSubscribed;
  function subscribeCartClose() {
    if (cartUpdateSubscribed) return;
    cartUpdateSubscribed = true;
    window.addEventListener('tf:cart:updated', function () {
      document.querySelectorAll('.shop-from-videos-modal.is-open').forEach(closeModalByElement);
    });
  }

  function runInit() {
    document.querySelectorAll('.shop-from-videos').forEach(initSection);
    subscribeCartClose();
  }

  // Run on DOM ready or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
  } else {
    runInit();
  }

  // Re-init when theme editor loads a section (so new sections work in editor)
  document.addEventListener('shopify:section:load', function (e) {
    const target = e.target;
    const section = target && target.querySelector ? target.querySelector('.shop-from-videos') || target.closest('.shop-from-videos') : null;
    if (section) {
      section.dataset.sfvReady = '';
      initSection(section);
    }
  });
})();
